import { useEffect } from "react";
import { Crown, Coins, X } from "lucide-react";
import { imgSrc } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { confetti, playFanfare } from "@/lib/celebrate";

const CloseX = ({ onClick }) => (
  <button onClick={onClick} data-testid="goldrain-results-close" aria-label="Schließen" className="absolute top-3 right-3 p-2 rounded-full bg-white/15 text-white hover:bg-white/25"><X className="w-5 h-5" /></button>
);

const PLACES = [[1, "h-28", "from-amber-300 to-amber-500"], [0, "h-36", "from-yellow-200 to-amber-400"], [2, "h-20", "from-orange-300 to-amber-600"]];

function Step({ p, rank, h, grad }) {
  return (
    <div className="flex-1 flex flex-col items-center justify-end min-w-0" data-testid={`goldrain-podium-${rank + 1}`}>
      {p ? (
        <>
          {rank === 0 && <Crown className="w-7 h-7 text-amber-300 mb-1 gold-pulse" />}
          {p.picture ? <img src={imgSrc(p.picture)} alt="" className="w-14 h-14 rounded-full object-cover border-4 border-amber-300" />
            : <div className="w-14 h-14 rounded-full bg-amber-200 border-4 border-amber-300 flex items-center justify-center font-black text-amber-800 text-xl">{p.name?.[0] || "?"}</div>}
          <p className="mt-1 text-xs font-bold text-white truncate max-w-full px-1">{p.name}</p>
          <p className="text-[11px] font-extrabold text-amber-200">{p.coins} Münzen</p>
        </>
      ) : <p className="text-xs text-white/50 mb-2">–</p>}
      <div className={`mt-2 w-full ${h} rounded-t-2xl bg-gradient-to-b ${grad} flex items-start justify-center pt-2 font-display text-3xl font-black text-amber-950 shadow-lg`}>{rank + 1}</div>
    </div>
  );
}

export const Podium = ({ top }) => (
  <div className="flex items-end gap-2">
    {PLACES.map(([r, h, g]) => <Step key={r} p={top[r]} rank={r} h={h} grad={g} />)}
  </div>
);

export function GoldRainResults({ ev, onClose }) {
  const { user } = useAuth();
  useEffect(() => {
    playFanfare();
    [0, 350, 700].forEach((d, i) => setTimeout(() => confetti({ x: innerWidth * (0.25 + 0.25 * i), y: innerHeight * 0.3, count: 90, colors: ["#fbbf24", "#f59e0b", "#fde68a", "#fb7185"] }), d));
  }, []);
  const mine = ev.top.findIndex((p) => p.user_id === user?.user_id);
  return (
    <div className="fixed inset-0 z-[96] flex items-center justify-center p-5 bg-amber-950/70 backdrop-blur-sm wheel-fade" data-testid="goldrain-results">
      <div className="relative w-full max-w-sm rounded-[2rem] bg-gradient-to-b from-rose-900 via-rose-950 to-black p-6 pt-8 border-2 border-amber-300/60 shadow-2xl shadow-amber-500/30 levelup-pop">
        <CloseX onClick={onClose} />
        <p className="text-center text-[11px] font-extrabold uppercase tracking-[0.35em] text-amber-300">Goldregen vorbei</p>
        <h2 className="text-center font-display text-3xl font-black text-white mt-1">Die besten Fänger</h2>
        <div className="mt-6"><Podium top={ev.top} /></div>
        <div className="mt-5 rounded-2xl bg-white/10 p-4 flex items-center justify-center gap-2 text-white" data-testid="goldrain-results-mine">
          <Coins className="w-5 h-5 text-amber-300" />
          <span className="text-sm font-semibold">Du hast <b className="text-amber-300">{ev.my_coins || 0}</b> Münzen gefangen = <b className="text-amber-300">+{ev.my_coins || 0} Punkte</b>{mine >= 0 && ` · Platz ${mine + 1}!`}</span>
        </div>
        <button onClick={onClose} data-testid="goldrain-results-ok" className="mt-4 w-full h-12 rounded-full gold-banner text-amber-950 font-extrabold">Super!</button>
      </div>
    </div>
  );
}
